import {
  MATCHING,
  STALENESS,
  TIMING,
  compassPoint,
  type CompassPoint,
  type ConvoyState,
  type NavigationEndReason,
  type PositionSample,
  type StatusMessageKind,
} from '@konvoi/shared';

/**
 * Deutsche Beschriftungen für die Zustände.
 *
 * Jeder Zustand hat ein Wort **und** ein Zeichen. Farbe kommt hinzu, steht aber
 * nie allein: Bei Sonne auf dem Display ist sie das Erste, was verloren geht.
 *
 * ## Kurz, weil sie in eine Zeile müssen
 *
 * „Anschluss verloren" und „Abseits der Strecke" brachen in der Kolonnenliste um
 * (21.08.2026, gemessen bei 375 px): zwei Zeilen in der Pille, jeder Eintrag
 * 21 Pixel höher. Sie heißen jetzt „Kein Anschluss" und „Abseits" — dieselbe
 * Aussage, und zusammen mit „Anschluss" und „Kein Signal" ein Satz, der sich im
 * Vorbeischauen lesen lässt. Was sie bedeuten, sagt die Erklärung darunter.
 *
 * Das Zeichen dazu steht in lib/zustandszeichen.tsx — gezeichnet statt aus der
 * Schrift geholt, damit alle dieselbe Fläche und dieselbe Strichstärke haben.
 *
 * ## „Zurückgefallen" und „Kein Anschluss" trennt die Zeit, nicht die Strecke
 *
 * Beide liegen über **derselben** Schwelle: `gapThresholdS` kommt in
 * `evaluateConvoyState` genau einmal vor. Verschieden ist allein, wie lange der
 * Rückstand schon darüber liegt — `gapHoldS` entscheidet, und das ist der Wert,
 * der Ampeln von echtem Anschlussverlust trennt.
 *
 * Bis zum 29.08.2026 stand hier „Hat Abstand, holt aber vermutlich wieder auf"
 * gegen „Ist **deutlich** zurück". Wer beides nebeneinander las, verstand zwei
 * Entfernungsstufen — und die gibt es nicht. Die Sätze sagen jetzt beide
 * „zu weit zurück" und unterscheiden sich in „erst seit Kurzem" gegen „bleibt".
 *
 * **Keine Zahlen in diesen Sätzen.** Die Schwellwerte sind je Ausfahrt
 * änderbar; eine Sekundenangabe im Text wäre dieselbe Zahl ein zweites Mal und
 * beim ersten Nachziehen falsch (Auftrag, Abschnitt 7).
 */

export interface StateLabel {
  readonly text: string;
  readonly art: 'gut' | 'achtung' | 'alarm' | 'ruhend';
  /** Ein Satz, der erklärt, was zu tun ist. */
  readonly erklaerung: string;
}

export const CONVOY_LABELS: Record<ConvoyState, StateLabel> = {
  in_contact: {
    text: 'Anschluss',
    art: 'gut',
    erklaerung: 'Fährt in der Kolonne.',
  },
  lagging: {
    text: 'Zurückgefallen',
    art: 'achtung',
    erklaerung: 'Ist zu weit zurück — aber erst seit Kurzem. Vielleicht nur eine Ampel.',
  },
  lost_contact: {
    text: 'Kein Anschluss',
    art: 'alarm',
    erklaerung: 'Bleibt zu weit zurück und kennt die Strecke womöglich nicht.',
  },
  off_route: {
    text: 'Abseits',
    art: 'alarm',
    erklaerung: 'Ist offenbar falsch abgebogen.',
  },
  standstill: {
    text: 'Steht',
    art: 'alarm',
    erklaerung: 'Steht seit einer Weile — womöglich eine Panne.',
  },
  no_signal: {
    text: 'Kein Signal',
    art: 'alarm',
    erklaerung: 'Meldet sich nicht. Vermutlich Funkloch.',
  },
  paused: {
    text: 'Pausiert',
    art: 'ruhend',
    erklaerung: 'Display gesperrt oder App im Hintergrund — sendet nicht.',
  },
};

/**
 * Die Beschriftungen der Meldungen. Das Zeichen dazu steht in
 * lib/meldezeichen.tsx — gezeichnet statt aus der Schrift geholt, damit alle
 * dieselbe Größe, dieselbe Strichstärke und keine Farbe haben.
 */
export const STATUS_LABELS: Record<StatusMessageKind, { text: string }> = {
  ok: { text: 'Alles in Ordnung' },
  break: { text: 'Pause nötig' },
  fuel: { text: 'Muss tanken' },
  breakdown: { text: 'Panne' },
  lost: { text: 'Verfahren' },
  slow_down: { text: 'Bitte langsamer' },

  // Nur die Führung setzt diese ab. Sie stehen in derselben Liste, weil es
  // dieselbe Art Nachricht ist — wer sie senden darf, steht in shared.
  stop: { text: 'Wir halten an' },
  break_soon: { text: 'Gleich Pause' },
  close_up: { text: 'Bitte aufschließen' },
  hazard: { text: 'Gefahrenstelle' },
  check_in: { text: 'Alles in Ordnung?' },
};

/**
 * Der Rückstand als Uhrzeitangabe — immer `MM:SS`.
 *
 * Die Form ist wichtiger, als sie aussieht: Sie ist die Hauptzahl auf dem
 * Bildschirm jedes Fahrzeugs, das nicht führt, und sie ändert sich im
 * Sekundentakt. Was dabei die Breite wechselt, zappelt — und was zappelt, zieht
 * den Blick von der Straße.
 *
 * Vorher gab es drei Formen: „45 s" unter einer Minute, „2 min" bei glatten
 * Minuten, „2:01 min" dazwischen. Bei jedem Wechsel sprang die Zahl, am
 * auffälligsten zwischen 2:00 und 2:01.
 *
 * Jetzt fünf Zeichen, immer dieselben, mit fester Ziffernbreite: `00:45`,
 * `02:01`, `12:05`. Die Einheit entfällt — die Doppelpunktform sagt von selbst,
 * dass Minuten und Sekunden gemeint sind, und daneben steht ohnehin, wovon die
 * Rede ist.
 */
export function formatDelay(seconds: number | null): string {
  return seconds === null ? '—' : formatMinutesSeconds(seconds);
}

/**
 * Eine Dauer als `MM:SS` — die Form, in der diese Anwendung Zeiten zeigt, die
 * sich laufend ändern.
 *
 * Zwei Stellen benutzen sie: der eigene Rückstand und die Restzeit der
 * Navigation. Beide zählen im Sekundentakt herunter, und beide standen vorher
 * in wechselnden Formen da — „7 min", dann „6 min", dann „45 s", jedes Mal
 * anders breit.
 *
 * **Nicht** für Altersangaben: „Position vor 2 min" und „vor 18 s" sind
 * Fließtext und lesen sich in Worten besser. Dafür gibt es formatDuration.
 */
export function formatMinutesSeconds(seconds: number): string {
  const gesamt = Math.max(0, Math.round(seconds));
  const minuten = Math.floor(gesamt / 60);
  const rest = gesamt % 60;

  return `${String(minuten).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
}

/**
 * Eine Entfernung, wie sie hier gelesen wird.
 *
 * Unter einem Kilometer in Metern und ohne Nachkommastelle: „auf 20 m genau"
 * ist bei GPS ohnehin die Grenze, und „437,2 m" sähe genauer aus, als es ist.
 * Darüber in Kilometern mit einer Stelle.
 *
 * **Komma, nicht Punkt.** Die Oberfläche ist deutsch (Leitfaden, Abschnitt 1),
 * und „3.4 km" ist es nicht. `toFixed` kennt nur den Punkt; bis zum 15.09.2026
 * stand er hier — die Funktion war bis dahin von niemandem benutzt, und der
 * Fehler wäre mit ihrer ersten Verwendung sichtbar geworden.
 */
export function formatDistance(meters: number | null): string {
  if (meters === null) {
    return '—';
  }
  if (meters < 1000) {
    return `${Math.round(meters)} m`;
  }
  return `${(meters / 1000).toLocaleString('de-DE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} km`;
}

/** Wie alt ist eine Meldung? Für die Kennzeichnung veralteter Positionen. */
export function formatDuration(ms: number): string {
  const sekunden = Math.round(ms / 1000);
  if (sekunden < 60) {
    return `${sekunden} s`;
  }
  const minuten = Math.round(sekunden / 60);
  return minuten < 60 ? `${minuten} min` : `${Math.round(minuten / 60)} h`;
}

export function formatAge(ms: number): string {
  return `vor ${formatDuration(ms)}`;
}

/**
 * Fahrzeug und Baujahr als ein Text — „Porsche 911 (1973)".
 *
 * Das Baujahr steht in Klammern und nicht hinter einem Schrägstrich: Es gehört
 * zum Fahrzeug, es steht nicht daneben. Mit Schrägstrich las sich beides wie
 * zwei gleichrangige Angaben, und beim Umbruch blieb die Jahreszahl allein in
 * der zweiten Zeile stehen.
 *
 * Beides ist freiwillig. Fehlt das Fahrzeug, steht die Jahreszahl für sich —
 * eine Klammer ohne davor wäre keine Klammer. Fehlt beides, kommt eine leere
 * Zeichenkette zurück, und der Aufrufer lässt die Zeile ganz aus.
 */
export function fahrzeugText(fahrzeug: {
  vehicleLabel: string | null;
  vehicleYear: number | null;
}): string {
  const bezeichnung = fahrzeug.vehicleLabel ?? '';
  const baujahr = fahrzeug.vehicleYear === null ? '' : String(fahrzeug.vehicleYear);

  if (bezeichnung === '') {
    return baujahr;
  }
  return baujahr === '' ? bezeichnung : `${bezeichnung} (${baujahr})`;
}

/** Kürzel für die Karte und die Liste: die ersten beiden Buchstaben. */
export function initials(name: string): string {
  const teile = name.trim().split(/\s+/);
  if (teile.length >= 2) {
    return `${teile[0]?.[0] ?? ''}${teile[1]?.[0] ?? ''}`.toUpperCase();
  }
  return name.trim().slice(0, 2).toUpperCase();
}

/**
 * Warum die Navigationsfläche dasteht — die zweite Zeile auf ihr.
 *
 * `null` heißt: kein Anlass, und damit steht die Fläche gar nicht erst da. Diese
 * Funktion ist die eine Stelle, an der beides entschieden wird — ob die Fläche
 * erscheint und was auf ihrer zweiten Zeile steht. Zwei getrennte Bedingungen
 * dafür liefen früher oder später auseinander.
 *
 * Drei Zustände geben keinen Anlass:
 *
 * - `in_contact` — es gibt nichts zurückzuführen.
 * - `paused` — die App liegt im Hintergrund, es sieht ohnehin niemand hin.
 * - `no_signal` — hier liegt der Grund tiefer. Ohne Ortung weiß niemand, wo das
 *   Fahrzeug ist; die letzte bekannte Position kann beliebig alt sein. Eine
 *   Route von dort wäre keine Auskunft, sondern eine Behauptung — und schlimmer
 *   als die Zahl, die aus demselben Grund unterbleibt (`delayIsKnown`), denn
 *   einer Route folgt der Fahrer. Am 25.08.2026 stand auf einem iPad in einer
 *   Tiefgarage „Navigation zum Führungsfahrzeug — Kein Signal", während die App
 *   beim Rückstand darüber ehrlich einen Strich zeigte. Die Führung sieht die
 *   Fläche für ein Fahrzeug ohne Signal aus demselben Grund nicht.
 *
 * Bewusst dieselben Wörter, die auch die Führung über dieses Fahrzeug sieht.
 * Ein eigener Satz für einen selbst („Du stehst") wäre freundlicher, aber dann
 * hätte dieselbe Sache zwei Namen, je nachdem wer draufschaut.
 */
export function navigationsAnlass(zustand: ConvoyState | null): string | null {
  if (
    zustand === null ||
    zustand === 'in_contact' ||
    zustand === 'paused' ||
    zustand === 'no_signal'
  ) {
    return null;
  }
  return CONVOY_LABELS[zustand].text;
}

/**
 * Warum eine Navigation endete — und was der Fahrer dazu lesen soll.
 *
 * `stopped` steht bewusst auf `null`: Wer selbst auf „beenden" getippt hat,
 * braucht keine Erklärung dafür. Ein Hinweis, der Selbstverständliches
 * bestätigt, ist einer zu viel — und wer sich an einen gewöhnt, liest den
 * nächsten nicht mehr.
 */
export const NAVIGATION_END_LABELS: Record<NavigationEndReason, string | null> = {
  arrived: 'Du bist wieder bei der Kolonne.',
  stopped: null,
  unavailable: 'Die Navigation ist gerade nicht möglich — der Routingdienst antwortet nicht.',
  no_route: 'Es ließ sich kein Weg zur Kolonne berechnen. Meldet die Führung ihre Position?',
};

/**
 * Welcher dieser Hinweise sich von selbst schließt.
 *
 * `arrived` bestätigt, was ohnehin gut ist: Das Fahrzeug ist wieder da, die
 * Navigation hat ihre Aufgabe erledigt. Dafür während der Fahrt einen
 * Fingertipp zu verlangen, ist eine Zumutung ohne Zweck.
 *
 * Die beiden anderen melden eine Störung. Sie bleiben stehen, bis jemand sie
 * gelesen hat — wer nicht weiß, dass der Routingdienst schweigt, wartet sonst
 * auf eine Route, die nie kommt.
 */
export const NAVIGATION_END_SELF_CLEARING: ReadonlySet<NavigationEndReason> = new Set<NavigationEndReason>(
  ['arrived'],
);

/**
 * Die Arten von Hinweisen, die unten am Bildschirm erscheinen können.
 *
 * Es sind Arten und keine Texte: Der Wortlaut kommt teils vom Server
 * (`serverfehler`) oder trägt eine Zahl (`messung_verworfen`). Was hier
 * entschieden wird, ist allein die Frage, ob der Hinweis einen Fingertipp
 * verlangt.
 */
export type HinweisArt =
  /** „Willkommen zurück. Die Standortübertragung läuft wieder." */
  | 'willkommen'
  /** Eine Messung war zu ungenau und ging gar nicht erst hinaus. */
  | 'messung_verworfen'
  /** Die Ortung meldet einen Fehler — kein Standort. */
  | 'ortungsfehler'
  /** Eine Fehlermeldung des Servers, im Wortlaut durchgereicht. */
  | 'serverfehler'
  /**
   * Die Verbindung kam nicht zustande — es wird weiter versucht.
   *
   * Eigene Art und nicht `serverfehler`, weil sich der Fall von selbst
   * erledigt: Das Protokoll führt `unauthorized` ausdrücklich als den Code, bei
   * dem „ein erneuter Versuch lohnt". Am 09.09.2026 stand deshalb nach einem
   * Funkloch ein Kasten mit „Verstanden" auf dem Bildschirm, den ein Fahrer bei
   * 66 km/h wegtippen sollte (A-106).
   */
  | 'verbindung'
  /** Aus der Kolonne genommen oder Ausfahrt beendet — die Fahrt ist vorbei. */
  | 'fahrt_vorbei'
  /** Ein Rollenwechsel ist nicht durchgekommen. */
  | 'rollenwechsel'
  /** Die Sichtbarkeit ließ sich nicht ändern. */
  | 'sichtbarkeit'
  /**
   * Ein Fingertipp ging nicht hinaus, weil keine Verbindung stand.
   *
   * Nicht nachgereicht, sondern gemeldet — und das ist eine Entscheidung, keine
   * Bequemlichkeit: Eine Warteschlange stellte die Absicht zu, wenn sie falsch
   * geworden ist. Bei der Navigation verbietet es der Auftrag ausdrücklich
   * (Abschnitt 2): „Nichts startet unaufgefordert von selbst; insbesondere
   * keine Navigation."
   */
  | 'nicht_gesendet'
  /**
   * Eine Statusmeldung wartet im Puffer auf die nächste Verbindung.
   *
   * Schließt sich von selbst: Der Hinweis sagt, dass etwas geschieht, und in
   * der Zustandsleiste steht daneben dauerhaft, wie viele warten. Ein Kasten
   * zum Wegtippen wäre hier das Letzte, was ein Fahrer bei 70 km/h braucht.
   */
  | 'meldung_wartet'
  /** Die Ausfahrt ließ sich nicht beenden. */
  | 'ausfahrt_beenden';

/**
 * Schließt sich dieser Hinweis nach `LAYOUT.selbstschliessendMs` von selbst?
 *
 * ## Warum das eine Tabelle ist und keine neun Wahrheitswerte
 *
 * Bis zum 03.09.2026 stand die Entscheidung an **neun Stellen** im Zustand, je
 * als `hintSelfClearing: true|false` neben dem Text. A-74 hat schon damals
 * davor gewarnt: Wer eine Stelle vergisst, dessen Störungshinweis erbt die Art
 * seines Vorgängers und verschwindet. Prüfen ließ sich das nicht — es gab keine
 * Regel, nur neun Bekundungen.
 *
 * Als vollständige Tabelle über `HinweisArt` erzwingt der Übersetzer die
 * Entscheidung: Eine neue Art lässt sich nicht hinzufügen, ohne hier zu sagen,
 * ob sie einen Fingertipp verlangt.
 *
 * ## Der Maßstab
 *
 * Es geht **nicht** darum, ob eine Meldung gut oder schlecht ist, sondern ob
 * sie etwas verlangt:
 *
 * Selbstschließend ist, was sich von allein erledigt. „Willkommen zurück"
 * bestätigt nur, dass wieder läuft, was laufen soll. Eine verworfene Messung
 * ist mit der nächsten vorbei — sie erklärt eine Lücke in der eigenen Spur und
 * verlangt nichts (geändert am 03.09.2026 auf Markus' Hinweis vom Gerät).
 *
 * **Der Kartenfehler steht hier nicht**, obwohl A-74 ihn nennt: Er läuft nicht
 * über `hint`, sondern über einen eigenen Zustand in der Fahrtseite und bleibt
 * dort ohnehin stehen. Eine Art dafür wäre ein Eintrag, den niemand benutzt.
 *
 * Stehen bleibt, was einen zweiten Versuch braucht oder wo der Zustand bestehen
 * bleibt, bis jemand etwas tut: ein Ortungs- oder Kartenfehler, ein
 * fehlgeschlagener Rollen- oder Sichtbarkeitswechsel. Und `fahrt_vorbei` erst
 * recht — dort trägt der Hinweis die Schaltfläche zurück zum Anfang, sie darf
 * nicht verschwinden.
 */
const SELBSTSCHLIESSEND: Record<HinweisArt, boolean> = {
  willkommen: true,
  messung_verworfen: true,
  verbindung: true,
  meldung_wartet: true,

  ortungsfehler: false,
  serverfehler: false,
  fahrt_vorbei: false,
  rollenwechsel: false,
  sichtbarkeit: false,
  ausfahrt_beenden: false,
  nicht_gesendet: false,
};

export function hinweisSchliesstSelbst(art: HinweisArt): boolean {
  return SELBSTSCHLIESSEND[art];
}

/**
 * Ist eine Position veraltet?
 *
 * ## Warum das eine Funktion ist und keine zwei Einzeiler
 *
 * Bis zum 03.09.2026 stand die Frage an zwei Stellen in `DrivePage.tsx` —
 * zwanzig Zeilen auseinander und **entgegengesetzt** beantwortet:
 *
 * ```ts
 * const veraltet = gemessenAm === null || … > staleMs;   // Fahrtwerte: unbekannt = veraltet
 * const veraltet = alter !== null && alter > staleMs;    // Kolonnenliste: unbekannt = frisch
 * ```
 *
 * Die zweite war falsch, und sie hat zusammen mit A-99 dafür gesorgt, dass ein
 * Fahrzeug ohne bekanntes Alter in der Liste unauffällig blieb.
 *
 * **Unbekannt gilt als veraltet.** Abschnitt 10.1 des Auftrags lässt dazu
 * keinen Ermessensspielraum: *„Eine veraltete Position darf niemals wie eine
 * aktuelle dargestellt werden."* „Ich weiß es nicht" ist kein Beleg für
 * „frisch" — und der Fehler fällt so zur sicheren Seite.
 */
export function istVeraltet(alterMs: number | null): boolean {
  return alterMs === null || !Number.isFinite(alterMs) || alterMs > STALENESS.staleMs;
}

export interface Fahrtwerte {
  /** Gerundet in km/h; `null`, wenn nichts Brauchbares vorliegt. */
  readonly tempo: number | null;
  /** Himmelsrichtung, sonst `null` — unter 11 km/h ist der GPS-Kurs Rauschen. */
  readonly richtung: CompassPoint | null;
  readonly kursGrad: number | undefined;
  /** Was statt der Richtung dasteht: „steht", „kein Signal", „Tempo fehlt" oder ein Strich. */
  readonly wort: string;
}

/**
 * Tempo und Fahrtrichtung aus der eigenen Messung.
 *
 * Herausgelöst, weil es die Angaben an zwei Stellen gibt: im eigenen Kasten
 * oben rechts und — sobald eine Navigation läuft — in der Fußzeile des
 * Abbiegehinweises. Die Regeln dahinter gelten an beiden Stellen gleich und
 * dürfen deshalb nur einmal dastehen.
 *
 * Seit dem 04.09.2026 steht die Rechnung hier und nicht mehr in `DrivePage`:
 * Abschnitt 5.2 des Auftrags verlangt Fachlogik außerhalb der Komponenten, und
 * ohne das war das Wort unten nicht prüfbar.
 *
 * ## „kein Signal" heißt genau eine Sache
 *
 * Das Wort ist einer der sieben Zustände aus Abschnitt 10 und bedeutet dort:
 * von diesem Gerät kommt nichts mehr. Bis zum 04.09.2026 stand es hier auch
 * dann, wenn die Position **frisch** war und allein das Tempo fehlte — und das
 * ist ein anderer Fall: Die Schnittstelle erlaubt `coords.speed === null`
 * ausdrücklich, `geolocation.ts` hat dafür einen eigenen Zweig, und dann liegt
 * der Ort vor und nur die Geschwindigkeit nicht. Aufgefallen im Browserlauf am
 * 04.09.2026, wo ein vorgetäuschter Standort nie ein Tempo mitliefert (A-103).
 *
 * Deshalb sind es jetzt zwei Wörter:
 *
 *   * **„kein Signal"** — keine Messung oder eine veraltete. Der Zustand.
 *   * **„Tempo fehlt"** — Ort frisch, Geschwindigkeit unbekannt. Genau so lang
 *     wie das andere, damit der Kasten seine Breite behält.
 */
export function berechneFahrtwerte(
  messung: PositionSample | null,
  gemessenAm: number | null,
  jetzt: number,
): Fahrtwerte {
  const veraltet = istVeraltet(gemessenAm === null ? null : jetzt - gemessenAm);
  const tempoMps = veraltet ? undefined : messung?.speedMps;
  const kursGrad = veraltet ? undefined : messung?.headingDeg;

  const tempo = tempoMps === undefined ? null : Math.round(tempoMps * 3.6);
  const kursTaugt =
    tempoMps !== undefined && kursGrad !== undefined && tempoMps >= MATCHING.courseMinSpeedMps;
  const richtung = kursTaugt && kursGrad !== undefined ? compassPoint(kursGrad) : null;

  const wort = ((): string => {
    if (richtung !== null) {
      return richtung;
    }
    if (messung === null || veraltet) {
      return 'kein Signal';
    }
    if (tempoMps === undefined) {
      return 'Tempo fehlt';
    }
    return tempoMps < TIMING.movingSpeedMps ? 'steht' : '—';
  })();

  return { tempo, richtung, kursGrad, wort };
}
